'use client'
import { motion, AnimatePresence } from "framer-motion"
import { useState, useEffect, Suspense, lazy } from "react"
import { useTranslations } from "next-intl"
import LanguageSwitcher from "@/components/LanguageSwitcher"
import ThemeSwitcher from "@/components/ThemeSwitcher"
import Footer from "@/components/Footer"
import dynamic from "next/dynamic"

const BlockchainScene = dynamic(
  () => import("@/components/BlockchainScene"),
  { 
    ssr: false,
    loading: () => null
  }
);
const Portfolio = lazy(() => import("@/components/Portfolio"));

export default function Home() {
  const [showPortfolio, setShowPortfolio] = useState(false)
  const [isMobile, setIsMobile] = useState(true)
  const translation = useTranslations();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const handleScrollTransition = () => {
    setShowPortfolio(true)
  }

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        setShowPortfolio(true)
      }
    }

    window.addEventListener('wheel', handleWheel)
    return () => window.removeEventListener('wheel', handleWheel)
  }, [])

  return (
    <div className={`font-body w-full relative`}>
      {/* Theme Switcher & Language Switcher */}
      <div className={`absolute md:fixed top-4 right-4 z-50 flex items-center gap-4 ${showPortfolio ? 'hidden md:flex' : 'flex'}`}>
        <ThemeSwitcher />
        <LanguageSwitcher />
      </div>
      <div className="flex flex-col md:flex-row w-full h-screen bg-[var(--background)] relative z-0">
        <div className="w-full md:w-2/3 h-1/2 md:h-full relative z-0">
          <Suspense fallback={null}>
            <BlockchainScene />
          </Suspense>
        </div>
        <div className="font-display w-full md:w-1/3 h-1/2 md:h-full z-10 flex flex-col justify-center items-center md:items-start text-[var(--foreground)] px-4 sm:px-6 md:px-16 text-center md:text-left py-6 md:py-0">
          <h1 className="uppercase text-2xl sm:text-3xl md:text-5xl font-bold leading-tight mb-6">
            {translation('hero.title')}
          </h1>
          <button
            onClick={handleScrollTransition}
            className="uppercase cursor-pointer mt-4 px-5 sm:px-6 py-2 border border-accent text-accent text-sm sm:text-base md:text-lg hover:bg-accent hover:text-black transition duration-300"
          >
            {translation('hero.enter')}
          </button>
        </div>

        <div
          className="hidden md:block absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20 cursor-pointer animate-bounce"
          onClick={handleScrollTransition}
        >
          <div className="w-6 h-6 border-t-2 border-l-2 border-accent rotate-45" />
        </div>
      </div>
      <div className="font-body fixed bottom-2 sm:bottom-4 left-2 sm:left-4 text-[var(--foreground)] text-xs z-30 pointer-events-none">
        {isMobile ? translation('hero.tipMobile') : translation('hero.tipDesktop')}
      </div>
      <Footer position="fixed"/>
      <AnimatePresence>
        {showPortfolio && (
          <motion.section
            initial={{ y: "100vh" }}
            animate={{ y: 0 }}
            exit={{ y: "100vh" }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="fixed top-0 left-0 w-full h-[100dvh] bg-[var(--background)] text-[var(--foreground)] flex flex-col md:justify-center md:items-center z-30 overflow-y-auto md:overflow-hidden"
            style={{ touchAction: 'pan-y' }}
          >
            <Portfolio onClose={() => setShowPortfolio(false)} />
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  )
}
